import React, { useState, useMemo } from 'react';

interface CompanyLogoProps {
  symbol: string;
  name?: string;
  size?: number;
  shape?: 'rounded' | 'circle';
  className?: string;
  style?: React.CSSProperties;
}

const GRADIENTS = [
  'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)', // Indigo
  'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', // Sky
  'linear-gradient(135deg, #059669 0%, #047857 100%)', // Emerald
  'linear-gradient(135deg, #d97706 0%, #b45309 100%)', // Amber
  'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', // Violet
  'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', // Rose
  'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', // Cyan
  'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', // Blue
];

const getInitials = (symbol: string, name?: string): string => {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  return symbol.slice(0, 2).toUpperCase();
};

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  symbol,
  name,
  size = 28,
  shape = 'circle',
  className = '',
  style,
}) => {
  const [srcIndex, setSrcIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const cleanSymbol = useMemo(() => {
    return (symbol || '').trim().toUpperCase().replace(/-[A-Z0-9]+$/, '').replace(/\.(NS|BO)$/i, '');
  }, [symbol]);

  const logoSources = useMemo(() => {
    if (!cleanSymbol) return [];
    return [
      `/api/logo/${cleanSymbol}`,
      `https://images.5paisa.com/MarketIcons/${cleanSymbol}.png`,
      `https://cdn.jsdelivr.net/gh/dharunashokkumar/api.dharun.dev@main/data/logos/nse/NSE_${cleanSymbol}.svg`,
      `https://raw.githubusercontent.com/dharunashokkumar/api.dharun.dev/main/data/logos/nse/NSE_${cleanSymbol}.svg`,
    ];
  }, [cleanSymbol]);

  // Reset image load state and error state when cleanSymbol changes
  React.useEffect(() => {
    setSrcIndex(0);
    setHasError(false);
    setIsLoaded(false);
  }, [cleanSymbol]);

  const currentLogoUrl = logoSources[srcIndex] || '';

  const handleImageError = () => {
    if (srcIndex + 1 < logoSources.length) {
      setSrcIndex((prev) => prev + 1);
      setIsLoaded(false);
    } else {
      setHasError(true);
    }
  };

  const initials = useMemo(() => getInitials(cleanSymbol, name), [cleanSymbol, name]);

  const gradient = useMemo(() => {
    const idx = hashString(cleanSymbol) % GRADIENTS.length;
    return GRADIENTS[idx];
  }, [cleanSymbol]);

  const borderRadius = shape === 'circle' ? '50%' : `${Math.max(4, Math.round(size * 0.22))}px`;
  const fontSize = `${Math.max(10, Math.round(size * 0.38))}px`;

  return (
    <div
      className={`company-logo ${shape === 'circle' ? 'company-logo--circle' : 'company-logo--rounded'} ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        maxWidth: `${size}px`,
        maxHeight: `${size}px`,
        aspectRatio: '1 / 1',
        flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius,
        ...style,
      }}
      title={name || symbol}
      aria-label={`${name || symbol} logo`}
    >
      {!hasError && currentLogoUrl ? (
        <>
          <img
            key={currentLogoUrl}
            src={currentLogoUrl}
            alt={`${symbol} logo`}
            className={`company-logo__img ${isLoaded ? 'company-logo__img--loaded' : 'company-logo__img--hidden'}`}
            loading="eager"
            decoding="async"
            ref={(node) => {
              if (node && node.complete && node.naturalWidth > 0 && !isLoaded) {
                setIsLoaded(true);
              }
            }}
            onLoad={() => setIsLoaded(true)}
            onError={handleImageError}
          />
          {!isLoaded && (
            <div
              className="company-logo__fallback"
              style={{
                background: gradient,
                borderRadius,
                fontSize,
              }}
            >
              <span>{initials}</span>
            </div>
          )}
        </>
      ) : (
        <div
          className="company-logo__fallback"
          style={{
            background: gradient,
            borderRadius,
            fontSize,
          }}
        >
          <span>{initials}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(CompanyLogo);
